import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  MdSearch,
  MdLocationOn,
  MdVerifiedUser,
  MdStorefront,
  MdErrorOutline,
} from 'react-icons/md';
import {
  useDiscoverClientsQuery,
  useClaimClientMutation,
} from '@redux/modules/clientOwnership';
import styles from './discoverClients.module.scss';

const initials = (title = '') =>
  title
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || 'C';

export const DiscoverClients = () => {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  // Debounce search input
  React.useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isFetching, error, refetch } = useDiscoverClientsQuery({
    search: debounced,
    page,
    limit,
  });

  const [claimClient, { isLoading: claiming }] = useClaimClientMutation();
  const [claimingId, setClaimingId] = useState(null);

  const clients = data?.data?.clients || [];
  const total = data?.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const handleClaim = (client) => {
    setClaimingId(client.id);
    claimClient(client.id)
      .unwrap()
      .then((r) => {
        toast.success(r?.messages || 'Claim submitted');
        refetch();
      })
      .catch((e) => {
        const err = e?.data?.errors || e?.data?.message || 'Claim failed';
        toast.error(Array.isArray(err) ? err.join(', ') : String(err));
      })
      .finally(() => setClaimingId(null));
  };

  const errMessage =
    error?.data?.errors || error?.data?.message || 'Could not load clients.';
  const errStr = Array.isArray(errMessage) ? errMessage.join(', ') : String(errMessage);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h2>Discover Clients</h2>
          <p>Find and claim existing TMR clients to manage their jobs and notes.</p>
        </div>
        <div className={styles.searchBox}>
          <MdSearch />
          <input
            type="text"
            placeholder="Search clients by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {error ? (
        <div className={styles.error}>
          <MdErrorOutline />
          <p>{errStr}</p>
        </div>
      ) : isFetching ? (
        <div className={styles.loading}>
          <MdStorefront />
          <p>Loading clients…</p>
        </div>
      ) : clients.length === 0 ? (
        <div className={styles.empty}>
          <MdStorefront />
          <p>No claimable clients found.</p>
        </div>
      ) : (
        <>
          <div className={styles.grid}>
            {clients.map((client) => (
              <div key={client.id} className={styles.card}>
                <div className={styles.cardTop}>
                  {client.logo ? (
                    <img className={styles.logo} src={client.logo} alt={client.title} />
                  ) : (
                    <div className={styles.logoFallback}>{initials(client.title)}</div>
                  )}
                  <div className={styles.cardInfo}>
                    <div className={styles.cardTitle} title={client.title}>
                      {client.title}
                    </div>
                    {(client.city || client.country) && (
                      <div className={styles.cardMeta}>
                        <MdLocationOn />
                        {[client.city, client.country].filter(Boolean).join(', ')}
                      </div>
                    )}
                    <span
                      className={`${styles.cardTag} ${client.is_owned ? styles.tagOwned : styles.tagUnowned
                        }`}
                    >
                      {client.is_owned
                        ? `Owned by Account #${client.owner_account_id}`
                        : 'Unclaimed'}
                    </span>
                  </div>
                </div>
                <div className={styles.cardFooter}>
                  {client.is_owned ? (
                    <button
                      type="button"
                      className={styles.viewBtn}
                      disabled={claiming && claimingId === client.id}
                      onClick={() => handleClaim(client)}
                    >
                      {claiming && claimingId === client.id ? 'Requesting…' : 'Request Claim'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={styles.claimBtn}
                      disabled={claiming && claimingId === client.id}
                      onClick={() => handleClaim(client)}
                    >
                      <MdVerifiedUser />
                      {claiming && claimingId === client.id ? 'Claiming…' : 'Claim'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className={styles.pageInfo}>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
